import React from 'react'
import moment from 'moment'
import { Group } from '@visx/group'
import { curveBasis, curveLinear, curveStep } from '@visx/curve'
import { LinePath } from '@visx/shape'
import { Threshold } from '@visx/threshold'
import { scaleTime, scaleLinear } from '@visx/scale'
import { AxisLeft, AxisBottom } from '@visx/axis'
import { GridRows, GridColumns } from '@visx/grid'
import merge from 'lodash/merge'

export const background = '#f3f3f3'

type TooltipData = {
    bar: any
    key: any
    index: number
    height: number
    width: number
    x: number
    y: number
    color: string
}

// accessors

// scales

const defaultMargin = { top: 40, right: 30, bottom: 50, left: 40 }

export type ThresholdProps = {
    data: any[]
    tickFormat?: (d) => string
    valueKeys: { key: string; label: string }[]
    xAxisKey: string
    compAKey: string
    compBKey: string
    width: number
    height: number
    margin?: { top?: number; right?: number; bottom?: number; left?: number }
    ylabel?: string
    xlabel?: string
    ylabelOffset?: number
    xlabelOffset?: number
}

export default function DifferenceChart({
    data,
    compBKey,
    compAKey,
    xAxisKey,
    valueKeys,
    width,
    height,
    tickFormat,
    margin = {},
    ylabel,
    xlabel,
    ylabelOffset,
    xlabelOffset,
}: ThresholdProps) {
    if (width < 10) return null
    const date = (d) => new Date(d[xAxisKey]).valueOf()
    const _margin = merge(defaultMargin, margin)
    // bounds
    const xMax = width - _margin.left - _margin.right
    const yMax = height - _margin.top - _margin.bottom

    const getX = (d) => new Date(d[xAxisKey]).valueOf()

    const timeScale = scaleTime<number>({
        domain: [Math.min(...data.map(getX)), Math.max(...data.map(getX))],
    })

    const valueScale = scaleLinear<number>({
        domain: [
            Math.min(...data.map((d) => Math.min(d[compAKey], d[compBKey]))),
            Math.max(...data.map((d) => Math.max(d[compAKey], d[compBKey]))),
        ],
        nice: true,
    })
    const tickWidth = 90
    const tickCount = Math.floor(width / tickWidth)
    timeScale.range([0, xMax])
    valueScale.range([yMax, 0])
    const curve = curveBasis
    return (
        <div>
            <svg width={width} height={height}>
                {/*<rect x={0} y={0} width={width} height={height} fill={background} rx={14} />*/}
                <Group left={_margin.left} top={_margin.top}>
                    <GridRows scale={valueScale} width={xMax} height={yMax} stroke='#e0e0e0' />
                    <GridColumns scale={timeScale} width={xMax} height={yMax} stroke='#e0e0e0' />
                    <line x1={xMax} x2={xMax} y1={0} y2={yMax} stroke='#e0e0e0' />
                    <AxisBottom
                        tickFormat={(d) => {
                            if (tickFormat) {
                                return tickFormat(d)
                            }
                            // @ts-ignore
                            return moment(d).format('MM/YY')
                        }}
                        top={yMax}
                        scale={timeScale}
                        numTicks={tickCount}
                        label={xlabel}
                        labelOffset={xlabelOffset}
                    />
                    <AxisLeft label={ylabel} labelOffset={ylabelOffset} scale={valueScale} />
                    <Threshold<any>
                        id={`${Math.random()}`}
                        data={data}
                        x={(d) => timeScale(date(d)) ?? 0}
                        y0={(d) => valueScale(d[compAKey]) ?? 0}
                        y1={(d) => valueScale(d[compBKey]) ?? 0}
                        clipAboveTo={0}
                        clipBelowTo={yMax}
                        curve={curve}
                        belowAreaProps={{
                            fill: '#ff8282',
                            fillOpacity: 0.4,
                        }}
                        aboveAreaProps={{
                            fill: '#00a98a',
                            fillOpacity: 0.4,
                        }}
                    />
                    <LinePath
                        data={data}
                        curve={curve}
                        x={(d) => timeScale(date(d)) ?? 0}
                        y={(d) => valueScale(d[compBKey]) ?? 0}
                        stroke='#222'
                        strokeWidth={1.5}
                        strokeOpacity={0.8}
                        strokeDasharray='1,2'
                    />
                    <LinePath
                        data={data}
                        curve={curve}
                        x={(d) => timeScale(date(d)) ?? 0}
                        y={(d) => valueScale(d[compAKey]) ?? 0}
                        stroke='#222'
                        strokeWidth={1.5}
                    />
                </Group>
            </svg>
        </div>
    )
}
